import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Snackbar,
  IconButton,
  Stack,
  LinearProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faFileImport,
  faArrowRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import { downloadExcelTemplate } from "../../../utils/exportTemplate";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TpHeiDropdown from "../CourseCatalog/DropDowns/TpHeiDropdown";
import useFirestore from "../../../config/fireBaseHook";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";

// Helper to match existing boolean formatting (e.g., Fps, CourseCatalog)
const getBooleanDisplayValue = (value) => {
  if (value === null || typeof value === "undefined") return "";

  // Normalize to lowercase string and trim
  const str = String(value).trim().toLowerCase();

  // Numeric-style encodings
  if (str === "0" || str === "0.0") return "No";
  if (str === "-1" || str === "-1.0" || str === "1" || str === "1.0")
    return "Yes";

  // Optional Y/N encodings
  if (str === "y" || str === "yes") return "Yes";
  if (str === "n" || str === "no") return "No";

  return String(value);
};

const initialForm = {
  HEI_program_code: "",
  competitive_category: "",
  multi_pathway_cat: "",
  active: "-1",
  supp_cat: "",
  f_pretty_cat: "",
};

const ShowcaseCategories = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    HEI_program_code: "",
    competitive_category: "",
    multi_pathway_cat: "",
    Active: "",
    supp_cat: "",
    f_pretty_cat: "",
  });
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importStatus, setImportStatus] = useState({
    open: false,
    added: 0,
    updated: 0,
    total: 0,
    loading: false,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const COLLECTION_NAME = "ShowcaseCategories";

  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      let rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by HEI_program_code alphabetically
      rows = rows.sort((a, b) =>
        (a.HEI_program_code || "").localeCompare(b.HEI_program_code || ""),
      );
      setData(rows);
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = data.filter((row) =>
      Object.keys(filter).every((key) => {
        if (key === "Active") {
          // Use row.active (lowercase) to match the actual data field
          const display = getBooleanDisplayValue(row.active);
          if (filter.Active === "-1") {
            return display === "Yes";
          }
          if (filter.Active === "0") {
            return display === "No";
          }
          return true; // Show all if blank
        }
        return !filter[key]
          ? true
          : (row[key] || "")
              .toString()
              .toLowerCase()
              .includes(filter[key].toString().toLowerCase());
      }),
    );
    setFilteredData(filtered);
  }, [data, filter]);

  const handleOpenAdd = () => {
    setEditIndex(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const handleOpenEdit = (params) => {
    const idx = data.findIndex((d) => d.id === params.row.id);
    setEditIndex(idx);
    setForm(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Only ask for confirmation when editing an existing record — a fresh
  // Add form has nothing at risk yet. The X (and backdrop/Escape, which
  // share this same handler) go through the same check.
  const handleCloseAttempt = () => {
    if (editIndex !== null) {
      setConfirmCloseOpen(true);
    } else {
      handleCloseModal();
    }
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    handleCloseModal();
  };

  const handleFormChange = (e) => {
    let value = e.target.value;
    if (e.target.name === "active") {
      value = value;
    }
    setForm({ ...form, [e.target.name]: value });
  };

  const handleSave = async () => {
    // Basic required validation on key fields
    if (!form.HEI_program_code || !form.competitive_category) {
      setSnackbar({
        open: true,
        message: "HEI Program Code and Competitive Category are required.",
        severity: "error",
      });
      return;
    }
    try {
      if (editIndex !== null) {
        const docId = data[editIndex].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), form);
        const newData = [...data];
        newData[editIndex] = { ...form, id: docId };
        setData(newData);
        setSnackbar({
          open: true,
          message: "Showcase category updated",
          severity: "success",
        });
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), form);
        setData([...data, { ...form, id: docRef.id }]);
        setSnackbar({
          open: true,
          message: "Showcase category added",
          severity: "success",
        });
      }
      setModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving to Firestore",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (editIndex !== null) {
      const docId = data[editIndex].id;
      await deleteDoc(doc(db, COLLECTION_NAME, docId));
      const newData = data.filter((_, idx) => idx !== editIndex);
      setData(newData);
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "Showcase category deleted",
        severity: "success",
      });
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({
      open: true,
      added: 0,
      updated: 0,
      total: 0,
      loading: true,
    });
    const reader = new FileReader();

    const processExcelRow = async (row, merged, counts) => {
      const { added, updated } = counts;
      const idx = merged.findIndex(
        (d) =>
          d.HEI_program_code === row.HEI_program_code &&
          d.competitive_category === row.competitive_category,
      );
      if (idx > -1) {
        const docId = merged[idx].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), row);
        merged[idx] = { ...merged[idx], ...row };
        counts.updated = updated + 1;
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), row);
        merged.push({ ...row, id: docRef.id });
        counts.added = added + 1;
      }
    };

    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws);

      const merged = [...data];
      const counts = { added: 0, updated: 0 };

      for (const row of jsonData) {
        await processExcelRow(row, merged, counts);
        setImportStatus((s) => ({
          ...s,
          total: jsonData.length,
          added: counts.added,
          updated: counts.updated,
        }));
      }

      setData(merged);
      setSnackbar({
        open: true,
        message: "Excel data imported",
        severity: "success",
      });
      setImportStatus((s) => ({ ...s, loading: false }));
    };

    reader.readAsBinaryString(file);
  };

  const handleDeleteMultipleConfirmed = async () => {
    for (const id of rowSelectionModel) {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
    }
    setData(data.filter((row) => !rowSelectionModel.includes(row.id)));
    setRowSelectionModel([]);
    setConfirmDeleteOpen(false);
    setSnackbar({
      open: true,
      message: "Selected showcase categories deleted",
      severity: "success",
    });
  };

  const handleOpenConfirmDelete = () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  // Export actual data to Excel
  const handleExportExcel = () => {
    if (!filteredData.length) {
      setSnackbar({
        open: true,
        message: "No data to export",
        severity: "warning",
      });
      return;
    }
    const exportData = filteredData.map(({ id, ...row }) => row); // Remove id field
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ShowcaseCategories");
    XLSX.writeFile(workbook, "showcase-categories.xlsx");
  };

  const handleExportTemplate = () => {
    downloadExcelTemplate(
      Object.keys(initialForm),
      `${COLLECTION_NAME}-template`,
    );
  };

  const rowsForGrid = filteredData.map((row) => ({
    ...row,
    id: String(row.id),
  }));

  const columns = [
    {
      field: "HEI_program_code",
      headerName: "HEI Program Code",
      flex: 1,
      sortable: true,
    },
    {
      field: "competitive_category",
      headerName: "Competitive Category",
      flex: 1,
      sortable: true,
    },
    {
      field: "multi_pathway_cat",
      headerName: "Multi Pathway Category",
      flex: 1,
      sortable: true,
      renderCell: (params) =>
        params?.value !== undefined ? getBooleanDisplayValue(params.value) : "",
      valueFormatter: (value) =>
        value !== undefined && value !== null ? getBooleanDisplayValue(value) : "",
    },
    {
      field: "active",
      headerName: "Active",
      flex: 0.5,
      sortable: true,
      renderCell: (params) =>
        params?.value !== undefined ? getBooleanDisplayValue(params.value) : "",
      valueFormatter: (value) =>
        value !== undefined && value !== null ? getBooleanDisplayValue(value) : "",
    },
    {
      field: "supp_cat",
      headerName: "Supplemental Category",
      flex: 1,
      sortable: true,
      renderCell: (params) =>
        params?.value !== undefined ? getBooleanDisplayValue(params.value) : "",
      valueFormatter: (value) =>
        value !== undefined && value !== null ? getBooleanDisplayValue(value) : "",
    },
    {
      field: "f_pretty_cat",
      headerName: "Pretty Category",
      flex: 1,
      sortable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => handleOpenEdit(params)}
          size="small"
        >
          <EditIcon />
        </IconButton>
      ),
      width: 90,
    },
  ];

  // Get unique HEI_program_code and competitive_category options for dropdown filters
  const { data: heiPrograms } = useFirestore("HeiPrograms");

  // Mirror TpHeiDropdown: only active programs, sort by display name, map code->name
  const filteredHeiPrograms = heiPrograms
    ? heiPrograms.filter((p) => {
        const activeVal = p.active ?? p.Active;
        return activeVal === -1 || activeVal === "-1";
      })
    : [];

  const sortedHeiPrograms = filteredHeiPrograms
    ? [...filteredHeiPrograms].sort((a, b) => {
        const aName =
          a.HEIProgramName ||
          a.Program_Name ||
          a.ProgramName ||
          a.ProgramCode ||
          a.HEI_Program_Code ||
          a.HEIProgramCode ||
          a.id ||
          "";
        const bName =
          b.HEIProgramName ||
          b.Program_Name ||
          b.ProgramName ||
          b.ProgramCode ||
          b.HEI_Program_Code ||
          b.HEIProgramCode ||
          b.id ||
          "";
        return aName.localeCompare(bName);
      })
    : [];

  // Build list of active HEI program options (mirror TpHeiDropdown) - include all active programs
  const heiOptions = sortedHeiPrograms.map((p) => {
    const code =
      p.ProgramCode || p.HEI_Program_Code || p.HEIProgramCode || p.id || "";
    const name = p.HEIProgramName || p.Program_Name || p.ProgramName || code;
    return { code, name };
  });

  const competitiveOptions = Array.from(
    new Set(data.map((row) => row.competitive_category).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
        Showcase Categories Management
      </Typography>
      {/* Showcase-specific breadcrumb with links for Categories, Locations, Status, Submissions */}
      <TechPrepPortalMgtBreadCrumbs pageName="Showcase Categories" />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems="center"
        sx={{ mt: 3, mb: 1 }}
      >
        {/* HEI Program Code filter using TpHeiDropdown (mirrors add/edit dropdown) */}
        <Box sx={{ minWidth: 360 }}>
          <TpHeiDropdown
            label="Filter HEI Program Code"
            name="HEI_program_code"
            value={filter.HEI_program_code}
            onChange={(e) =>
              setFilter({ ...filter, HEI_program_code: e.target.value })
            }
            fullWidth={true}
            margin="none"
          />
        </Box>
        {/* Competitive Category filter as dropdown */}
        <TextField
          select
          label="Filter Competitive Category"
          name="competitive_category"
          value={filter.competitive_category}
          onChange={(e) =>
            setFilter({ ...filter, competitive_category: e.target.value })
          }
          size="small"
          sx={{ minWidth: 360 }}
        >
          <MenuItem value="">All</MenuItem>
          {competitiveOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Filter Pretty Category"
          name="f_pretty_cat"
          value={filter.f_pretty_cat}
          onChange={(e) =>
            setFilter({ ...filter, f_pretty_cat: e.target.value })
          }
          size="small"
          sx={{ minWidth: 360 }}
        />
      </Stack>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems="center"
        sx={{ mt: 0, mb: 3 }}
      >
        {/* Multi-Pathway filter buttons */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">Multi-Pathway:</Typography>
          <Button
            variant={filter.multi_pathway_cat === "" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, multi_pathway_cat: "" })}
          >
            All
          </Button>
          <Button
            variant={
              filter.multi_pathway_cat === "-1" ? "contained" : "outlined"
            }
            size="small"
            onClick={() => setFilter({ ...filter, multi_pathway_cat: "-1" })}
          >
            Yes
          </Button>
          <Button
            variant={
              filter.multi_pathway_cat === "0" ? "contained" : "outlined"
            }
            size="small"
            onClick={() => setFilter({ ...filter, multi_pathway_cat: "0" })}
          >
            No
          </Button>
        </Stack>
        {/* Active filter buttons */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">Active:</Typography>
          <Button
            variant={filter.Active === "" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, Active: "" })}
          >
            All
          </Button>
          <Button
            variant={filter.Active === "-1" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, Active: "-1" })}
          >
            Yes
          </Button>
          <Button
            variant={filter.Active === "0" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, Active: "0" })}
          >
            No
          </Button>
        </Stack>
        {/* Supplemental filter buttons */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">Supplemental:</Typography>
          <Button
            variant={filter.supp_cat === "" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, supp_cat: "" })}
          >
            All
          </Button>
          <Button
            variant={filter.supp_cat === "-1" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, supp_cat: "-1" })}
          >
            Yes
          </Button>
          <Button
            variant={filter.supp_cat === "0" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, supp_cat: "0" })}
          >
            No
          </Button>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add
        </Button>
        <IconButton
          onClick={() =>
            setFilter({
              HEI_program_code: "",
              competitive_category: "",
              multi_pathway_cat: "",
              Active: "",
              supp_cat: "",
              f_pretty_cat: "",
            })
          }
          size="medium"
        >
          <FontAwesomeIcon icon={faArrowRotateLeft} color="#ff9800" />
        </IconButton>
        <IconButton component="label" size="medium">
          <FontAwesomeIcon icon={faFileImport} color="#2e7d32" />
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleExcelUpload}
          />
        </IconButton>
        <IconButton onClick={handleExportExcel} size="medium">
          <FontAwesomeIcon icon={faFileDownload} color="#1976d2" />
        </IconButton>
        <Button
          variant="contained"
          color="success"
          startIcon={<FontAwesomeIcon icon={faFileExcel} />}
          sx={{ color: "white" }}
          onClick={handleExportTemplate}
        >
          Export Template
        </Button>
        <ClearCollectionButton collectionName="ShowcaseCategories" />
      </Stack>
      <Box
        sx={{
          height: 500,
          width: "100%",
          background: "#fff",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          onRowDoubleClick={handleOpenEdit}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            setRowSelectionModel(newSelectionModel);
          }}
        />
      </Box>
      <Dialog
        open={modalOpen}
        onClose={handleCloseAttempt}
        maxWidth="sm"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseAttempt}>
          {editIndex !== null
            ? "Edit Showcase Category"
            : "Add Showcase Category"}
        </ModalDialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TpHeiDropdown
              label="HEI Program Code"
              name="HEI_program_code"
              value={form.HEI_program_code}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Competitive Category"
              name="competitive_category"
              value={form.competitive_category}
              onChange={handleFormChange}
              required
              fullWidth
            />
            {/* Multi Pathway Category as Yes/No radio buttons */}
            <FormControl component="fieldset">
              <FormLabel component="legend">Multi Pathway Category</FormLabel>
              <RadioGroup
                row
                name="multi_pathway_cat"
                value={form.multi_pathway_cat}
                onChange={handleFormChange}
              >
                <FormControlLabel value="-1" control={<Radio />} label="Yes" />
                <FormControlLabel value="0" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            {/* Active field as Yes/No radio buttons (already present) */}
            <FormControl component="fieldset">
              <FormLabel component="legend">Active</FormLabel>
              <RadioGroup
                row
                name="active"
                value={form.active}
                onChange={handleFormChange}
              >
                <FormControlLabel value="-1" control={<Radio />} label="Yes" />
                <FormControlLabel value="0" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            {/* Supplemental Category as Yes/No radio buttons */}
            <FormControl component="fieldset">
              <FormLabel component="legend">Supplemental Category</FormLabel>
              <RadioGroup
                row
                name="supp_cat"
                value={form.supp_cat}
                onChange={handleFormChange}
              >
                <FormControlLabel value="-1" control={<Radio />} label="Yes" />
                <FormControlLabel value="0" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Pretty Category"
              name="f_pretty_cat"
              value={form.f_pretty_cat}
              onChange={handleFormChange}
              fullWidth
              InputProps={{ readOnly: editIndex !== null }}
            />
          </Stack>
        </DialogContent>
        <ModalActions
          onSave={handleSave}
          onDelete={editIndex !== null ? handleDelete : undefined}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteMultipleConfirmed}
        title="Delete Selected Showcase Categories"
        message={`Are you sure you want to delete ${
          rowSelectionModel.length
        } selected record${
          rowSelectionModel.length === 1 ? "" : "s"
        }? This action cannot be undone.`}
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
      {importStatus.open && (
        <Box sx={{ mt: 2 }}>
          {importStatus.loading && <LinearProgress />}
          <Alert severity="info" sx={{ mt: 1 }}>
            Importing Excel:{" "}
            {importStatus.loading ? "Processing..." : "Complete."} <br />
            Added: {importStatus.added} &nbsp; Updated: {importStatus.updated}{" "}
            &nbsp; Total: {importStatus.total}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default ShowcaseCategories;
